<script lang="ts">
	import { ChevronLeft, ChevronRight, X } from 'lucide-svelte';
	import { Button } from '@/components/ui/button';
	import type { CalendarView, KeyItem } from '@/types/calendar';
	import type { DateValue } from '@internationalized/date';
	import type { Snippet } from 'svelte';
	import { getToday, getPreviousPeriod, getNextPeriod, formatDateHeader, getEventColorClasses } from './calendar.utils';

	interface Props {
		currentDate: DateValue;
		currentView: CalendarView;
		onDateChange: (date: DateValue) => void;
		onViewChange: (view: CalendarView) => void;
		filters?: Snippet;
		legendItems?: KeyItem[];
		activeFilterKeys?: string[];
		onFilterToggle?: (key: string) => void;
		onFilterReset?: () => void;
		height?: number;
	}

	let { currentDate, currentView, onDateChange, onViewChange, filters, legendItems, activeFilterKeys = [], onFilterToggle, onFilterReset, height = $bindable(0) }: Props =
		$props();

	let hasActiveFilters = $derived(activeFilterKeys.length > 0);

	let scrollY = $state(0);
	let isStuck = $derived(scrollY > 0);

	function handlePrevious() {
		const newDate = getPreviousPeriod(currentDate, currentView);
		onDateChange(newDate);
	}

	function handleToday() {
		const today = getToday();
		onDateChange(today);
	}

	function handleNext() {
		const newDate = getNextPeriod(currentDate, currentView);
		onDateChange(newDate);
	}

	let dateLabel = $derived(formatDateHeader(currentDate, currentView));
</script>

<svelte:window bind:scrollY />

<div
	bind:clientHeight={height}
	class="sticky z-40 flex flex-col bg-background transition-all duration-200 {isStuck ? 'gap-0 py-1' : 'gap-2 py-4'}"
	style="top: var(--calendar-header-top, var(--header-height, 0px))"
>
	<!-- Top row: Navigation, date, filters, view toggle -->
	<div class="flex flex-wrap items-center justify-between transition-all duration-200 {isStuck ? 'gap-2' : 'gap-4'}">
		<div class="flex items-center transition-all duration-200 {isStuck ? 'gap-2' : 'gap-4'}">
			<!-- Navigation controls -->
			<div class="flex items-center">
				<Button variant="outline" size="icon" class="rounded-r-none border-r-0 transition-all duration-200 {isStuck ? 'h-7 w-7' : ''}" onclick={handlePrevious}>
					<ChevronLeft class="size-4" />
				</Button>
				<Button variant="outline" class="rounded-none transition-all duration-200 {isStuck ? 'h-7 px-2 text-xs' : ''}" onclick={handleToday}>Today</Button>
				<Button variant="outline" size="icon" class="rounded-l-none border-l-0 transition-all duration-200 {isStuck ? 'h-7 w-7' : ''}" onclick={handleNext}>
					<ChevronRight class="size-4" />
				</Button>
			</div>

			<!-- Date label -->
			<h2 class="font-semibold transition-all duration-200 {isStuck ? 'text-sm' : 'text-xl'}">
				{dateLabel}
			</h2>

			<!-- Optional filters slot -->
			{#if filters}
				<div class="flex items-center gap-2">
					<span class="text-sm text-muted-foreground">Show:</span>
					{@render filters()}
				</div>
			{/if}
		</div>

		<!-- View toggle -->
		<div class="flex items-center">
			<Button
				variant={currentView === 'day' ? 'default' : 'outline'}
				class="rounded-r-none border-r-0 transition-all duration-200 {isStuck ? 'h-7 px-2 text-xs' : ''}"
				onclick={() => onViewChange('day')}
			>
				Day
			</Button>
			<Button
				variant={currentView === 'week' ? 'default' : 'outline'}
				class="rounded-none transition-all duration-200 {isStuck ? 'h-7 px-2 text-xs' : ''}"
				onclick={() => onViewChange('week')}
			>
				Week
			</Button>
			<Button
				variant={currentView === 'month' ? 'default' : 'outline'}
				class="rounded-l-none border-l-0 transition-all duration-200 {isStuck ? 'h-7 px-2 text-xs' : ''}"
				onclick={() => onViewChange('month')}
			>
				Month
			</Button>
		</div>
	</div>

	<!-- Legend row: Clickable filter items -->
	{#if legendItems && legendItems.length > 0}
		<div class="flex flex-wrap items-center gap-x-1 gap-y-1 rounded-md transition-all duration-200 {isStuck ? 'px-2 py-0.5' : 'px-3 py-2'}">
			{#each legendItems as item (item.key)}
				{@const isActive = !hasActiveFilters || activeFilterKeys.includes(item.key)}
				<button
					type="button"
					class="flex items-center rounded-md transition-all duration-200 hover:bg-muted {isStuck ? 'gap-1 px-1.5 py-0.5' : 'gap-1.5 px-2.5 py-1'} {isActive ? 'opacity-100' : 'opacity-30'}"
					onclick={() => onFilterToggle?.(item.key)}
				>
					<div class="rounded transition-all duration-200 {isStuck ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'} {getEventColorClasses(item.color)}"></div>
					<span class="font-medium text-muted-foreground transition-all duration-200 {isStuck ? 'text-xs' : 'text-sm'}">{item.label}</span>
				</button>
			{/each}
			{#if hasActiveFilters}
				<button
					type="button"
					class="ml-2 flex items-center gap-1 rounded-md px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
					onclick={() => onFilterReset?.()}
				>
					<X class="h-3 w-3" />
					Reset
				</button>
			{/if}
		</div>
	{/if}
</div>